import React from "react";
import {
  Scale,
  CheckCircle2,
  FileText,
  Calendar,
  ShieldCheck,
  Briefcase,
  Users,
  LayoutGrid,
} from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import ShowcaseImageLightbox from "../../component/ShowcaseImageLightbox";
import "./about.css";
import dashboard from "../../assets/new-images/Dashboard.png";
import matterList from "../../assets/new-images/legal1.png";
import matterDetail from "../../assets/new-images/legal2.png";
import clientDocuments from "../../assets/Legal/legaldocuments.png";

const features = [
  {
    icon: <FileText size={26} />,
    title: "Client & Matter Files",
    description:
      "Centralize case files, agreements, litigation records, and client documents in one secure, structured directory.",
  },
  {
    icon: <Calendar size={26} />,
    title: "Deadline Tracking",
    description:
      "Track hearing dates, filing deadlines, and contract renewals with automated alerts and visual deadline calendars.",
  },
  {
    icon: <ShieldCheck size={26} />,
    title: "Contract Workflows",
    description:
      "Streamline contract drafting, reviews, and sign-offs across internal associates and external stakeholders.",
  },
  {
    icon: <Briefcase size={26} />,
    title: "Matter Management",
    description:
      "Track every active case, transaction, or advisory matter by status, client, assignee, and critical milestone.",
  },
];

const LegalTeamsPage = () => {
  const benefits = [
    "Eliminate version control chaos across case files",
    "Track every client interaction and decision point",
    "Reduce time spent searching for documents by 70%",
    "Built-in approval workflows for contract reviews",
    "Secure client portals with granular permissions",
  ];

  const showcase = [
    {
      eyebrow: "Practice Dashboard",
      title: "See where every matter stands, every morning",
      description:
        "Today's tasks, a simple performance percentage, and a live breakdown across To Do, In Progress, Under Review, and Closed — plus upcoming events and reminders, all on one dashboard so partners start the day already knowing what needs attention.",
      image: dashboard,
      alt: "Simplifyte dashboard showing today's tasks, performance percentage, task status breakdown, and upcoming events and reminders",
    },
    {
      eyebrow: "Matter Tracking",
      title: "Every matter, from due diligence to litigation, in one board",
      description:
        "Due diligence, IP filings, arbitration, compliance deadlines, contract reviews, property disputes — track every matter as a task with its client, status, period, and assignee, so partners always know exactly where each case stands.",
      image: matterList,
      alt: "Simplifyte task table showing legal matters including Due Diligence, IP Filing, Arbitration, Compliance, Contract Review, and Litigation with client, status, and assignee columns",
    },
    {
      eyebrow: "Matter-level detail",
      title: "Every matter, with the client and deadline context that matters",
      description:
        "Open any matter to see its client, financial year, due date, assignees, and logged time at a glance — so a task like 'Compile evidence bundle for hearing' on the Bhavsar Construction arbitration carries exactly the context a partner needs, without a separate lookup.",
      image: matterDetail,
      alt: "Simplifyte task detail panel showing a Compile evidence bundle for hearing task under Arbitration - Bhavsar Construction, with client, financial year, due date, and assignees",
    },
    {
      eyebrow: "Client Documents",
      title: "Every client's case files, organized by matter",
      description:
        "Contract reviews, litigation cases, IP filings, employment disputes, and regulatory matters — each client gets its own folder with upload tracking, client visibility controls, and a clear record of who shared what and when.",
      image: clientDocuments,
      alt: "Simplifyte Client Documents screen showing legal folders for Acme Corporation Contract Review, TechNova Intellectual Property, ABC Holdings Litigation Case, and other client matters with upload and share details",
    },
  ];

  return (
    <>
      <HeaderMenu />
      <div className="Legal_Team_main">
        {/* Navigation */}
        {/* <div className="Legal_Team_nav">
        <div className="Legal_Team_container">
          <Link to="/" className="Legal_Team_back_link">
            <ArrowLeft size={18} />
            Back to About
          </Link>
        </div>
      </div> */}

        {/* Hero */}
        <section className="Legal_Team_hero">
          <div className="Legal_Team_container">
            <div className="Legal_Team_hero_content">
              <div className="Legal_Team_icon_box">
                <Scale size={34} />
              </div>

              <h1 className="Legal_Team_heading">
                Workflow management for legal teams
              </h1>

              <p className="Legal_Team_subheading">
                Keep case files, client communication, and internal review
                cycles tightly organized in a secure, central hub.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="Legal_Team_features_section">
          <div className="Legal_Team_container">
            <h2 className="Legal_Team_section_title Legal_Team_center">
              Everything legal teams need in one platform
            </h2>

            <div className="Legal_Team_features_grid">
              {features.map((feature, index) => (
                <div key={index} className="Legal_Team_feature_card">
                  <div className="Legal_Team_feature_icon">
                    {feature.icon}
                  </div>

                  <h3 className="Legal_Team_feature_title">
                    {feature.title}
                  </h3>

                  <p className="Legal_Team_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="Legal_Team_showcase_section">
          <div className="Legal_Team_container">
            <h2 className="Legal_Team_section_title Legal_Team_center">
              Built for the demands of legal practice
            </h2>
            <p className="Legal_Team_showcase_subtitle">
              See how legal teams organize case files, track client work,
              and run their practice on Simplifyte.
            </p>

            <div className="Legal_Team_showcase_list">
              {showcase.map((item, index) => (
                <div
                  key={index}
                  className={`Legal_Team_showcase_row ${
                    index % 2 === 1 ? "Legal_Team_showcase_row_reverse" : ""
                  }`}
                >
                  <div className="Legal_Team_showcase_text">
                    <span className="Legal_Team_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="Legal_Team_showcase_title">
                      {item.title}
                    </h3>
                    <p className="Legal_Team_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="Legal_Team_showcase_media">
                    <div className="Legal_Team_showcase_frame_outer">
                      <div className="Legal_Team_showcase_frame_inner">
                        <ShowcaseImageLightbox
                          src={item.image}
                          alt={item.alt}
                          className="Legal_Team_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="Legal_Team_benefits_section">
          <div className="Legal_Team_container">
            <div className="Legal_Team_benefits_wrapper">
              <h2 className="Legal_Team_section_title Legal_Team_center">
                Why legal teams choose Simplifyte
              </h2>

              <div className="Legal_Team_benefits_list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="Legal_Team_benefit_item">
                    <CheckCircle2 size={24} className="Legal_Team_check_icon" />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="Legal_Team_cta_section">
          <div className="Legal_Team_container">
            <div className="Legal_Team_cta_box">
              <h2 className="Legal_Team_cta_title">
                Ready to bring order to your practice?
              </h2>

              <p className="Legal_Team_cta_text">
                Join legal teams who've streamlined their case management and
                client communication.
              </p>

              <button
                className="Legal_Team_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
};

export default LegalTeamsPage;
